import { useEffect, useRef, useState } from "react"

export interface CitySuggestion {
  cityCode: string
  city: string
  country: string
}

/** Shape of a raw autocomplete row from the Pelikan API. */
interface PelikanItem {
  airportCode?: string
  cityCode: string
  city: string
  country?: string
}

const PELIKAN_URL = "https://www.pelikan.sk/gf1/pelijee-sk/mobile/autocomplete"
const DEBOUNCE_MS = 250

export function usePelikanAutocomplete(query: string) {
  const [suggestions, setSuggestions] = useState<CitySuggestion[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const controllerRef = useRef<AbortController | null>(null)
  const trimmed = query.trim()

  useEffect(() => {
    if (!trimmed) return

    const timer = setTimeout(() => {
      controllerRef.current?.abort()
      const controller = new AbortController()
      controllerRef.current = controller
      setIsLoading(true)

      const url = `${PELIKAN_URL}?q=${encodeURIComponent(trimmed)}&lang=en`

      fetch(url, { signal: controller.signal })
        .then((res) => {
          const contentType = res.headers.get("content-type") ?? ""
          return res.ok && contentType.includes("application/json") ? res.json() : []
        })
        .then((data: unknown): CitySuggestion[] => {
          if (!Array.isArray(data)) return []
          return (data as PelikanItem[])
            .filter((item) => !item.airportCode)
            .map((item) => ({
              cityCode: item.cityCode,
              city: item.city,
              country: item.country ?? "",
            }))
        })
        .then((cities) => {
          if (!controller.signal.aborted) {
            setSuggestions(cities)
            setIsLoading(false)
          }
        })
        .catch(() => {
          if (!controller.signal.aborted) {
            setSuggestions([])
            setIsLoading(false)
          }
        })
    }, DEBOUNCE_MS)

    return () => {
      clearTimeout(timer)
      controllerRef.current?.abort()
    }
  }, [trimmed])

  // When the query is blank, report nothing — derived from the query rather than
  // cleared via a synchronous setState inside the effect.
  return trimmed
    ? { suggestions, isLoading }
    : { suggestions: [] as CitySuggestion[], isLoading: false }
}
